import { format, isSameDay } from 'date-fns';
import { t } from 'utils/getTranslations';
import { dateStringToDateObject } from 'utils/parseDate';
import { TimecardGroupKeyOf } from '../TimecardList';

/** Left-to-right order of the `Group by:` control. */
export const TIMECARD_GROUP_BY_OPTIONS = ['none', 'date', 'author', 'case', 'client'] as const;

export type TimecardGroupBy = (typeof TIMECARD_GROUP_BY_OPTIONS)[number];

/** Each option's caption in the `Group by:` control. */
export const TIMECARD_GROUP_BY_LABEL: Record<TimecardGroupBy, string> = {
    none: 'timecards.list.group-by-none',
    date: 'timecards.list.group-by-date',
    author: 'timecards.list.group-by-author',
    case: 'timecards.list.group-by-case',
    client: 'timecards.list.group-by-client',
};

/** Grouping by date reproduces the card list this table replaces. */
export const DEFAULT_TIMECARD_GROUP_BY: TimecardGroupBy = 'date';

/** The list's default order, and its tie-breaker within every group. */
export const DEFAULT_TIMECARD_SORT = 'timecardDate,desc';

/**
 * The server sort each grouping forces, label first and then the group's own id.
 *
 * Rows have to arrive grouped: the list pages 50 at a time, so a group whose rows are scattered
 * through the result set would open a fresh ribbon on every page. The id is what makes that hold when
 * two groups share a label — two cases with the same number and name, two employees with the same
 * full name — which the label sort alone would interleave, since a group is identified by id and
 * would then be split across two runs.
 */
const GROUP_SORT: Record<Exclude<TimecardGroupBy, 'none'>, readonly string[]> = {
    // The date IS the group key, so ordering by it needs no tie-breaker to keep a day whole.
    date: [DEFAULT_TIMECARD_SORT],
    author: ['employee.fullName,asc', 'employee.id,asc'],
    case: ['matter.fullName,asc', 'matter.id,asc'],
    client: ['matter.clientParty.name,asc', 'matter.clientParty.id,asc'],
};

/** Which grouping each option belongs to, as the per-surface `disabledGroups` prop names it. */
export const GROUP_BY_FILTER_GROUP: Partial<
    Record<TimecardGroupBy, 'timecards.group.case' | 'timecards.group.client'>
> = {
    case: 'timecards.group.case',
    client: 'timecards.group.client',
};

function sortField(sort: string): string {
    return sort.split(',')[0] ?? '';
}

/**
 * The `sort` params one fetch sends: the group's sort first, then the user's column sort, then the
 * date as a tie-breaker.
 *
 * The group leads so its rows cannot straddle a page boundary, which leaves the column sort ordering
 * rows WITHIN each group — except when the user sorted by the very field the grouping labels itself
 * by, where their direction replaces the label sort outright. The id tie-breaker survives that
 * substitution, because it is what keeps two same-labelled groups apart either way.
 */
export function composeTimecardSort(groupBy: TimecardGroupBy, columnSort: string): string[] {
    const sorts: string[] = [];
    if (groupBy !== 'none') {
        const [labelSort, ...tieBreakers] = GROUP_SORT[groupBy];
        sorts.push(sortField(labelSort) === sortField(columnSort) ? columnSort : labelSort, ...tieBreakers);
    }
    if (columnSort && !sorts.includes(columnSort)) {
        sorts.push(columnSort);
    }
    if (!sorts.some((sort) => sortField(sort) === sortField(DEFAULT_TIMECARD_SORT))) {
        sorts.push(DEFAULT_TIMECARD_SORT);
    }
    return sorts;
}

/**
 * What identifies a row's group. Ids rather than display names wherever there is one, so two clients
 * that share a name stay two groups.
 */
export function timecardGroupKeyOf(groupBy: TimecardGroupBy): TimecardGroupKeyOf {
    switch (groupBy) {
        case 'date':
            return (timecard) => timecard.timecardDate;
        case 'author':
            return (timecard) => String(timecard.employee?.id ?? '');
        case 'case':
            return (timecard) => String(timecard.matter?.id ?? '');
        case 'client':
            return (timecard) => String(timecard.matter?.clientParty?.id ?? '');
        // Flat list: every row lands in the one group, whose ribbon is not rendered.
        case 'none':
        default:
            return () => '';
    }
}

/**
 * A date group's heading: today by name, any other day by weekday, both followed by the date in the
 * main company's own format. The weekday pattern is a translation because its abbreviation is
 * language-specific; the date half never is, since the tenant chose that format.
 */
export function formatGroupDate(timecardDate: string, dateFormat: string): string {
    const date = dateStringToDateObject(timecardDate);
    const weekday = isSameDay(date, new Date())
        ? t('time.today')
        : format(date, t('timecards.list.group-date-weekday'));
    return `${weekday}, ${format(date, dateFormat)}`;
}
